<template>
  <div class="modal fade show" style="display: block; background: #1c1d1c33;">
    <div
      class="modal-dialog modal-dialog-centered modal-xl draggable-dialog"
      ref="dialogRef"
      :style="dialogStyle"
    >
      <div class="modal-content">
        <div
          class="d-flex justify-content-between px-2 my-1 align-item-center draggable-handle"
          @mousedown="onMouseDown"
        >
          <h4 class="mb-0">Voucher Details</h4>
          <button @click="onClickClose" type="button" class="btn-close" aria-label="Close"></button>
        </div>
        <hr class="mt-0 mb-0">

        <div v-if="loadingDetail" class="text-center py-3">Loading...</div>

        <div v-if="! loadingDetail && detail" class="p-2 modal-scroll-body">
          <div class="row mb-2">
            <div class="col-md-4">
              <label class="text-muted">Voucher No</label>
              <div class="fw-bold">{{ detail.voucher_no }}</div>
            </div>
            <div class="col-md-4">
              <label class="text-muted">Date</label>
              <div class="fw-bold">{{ detail.date }}</div>
            </div>
            <div class="col-md-4">
              <label class="text-muted">Party</label>
              <div class="fw-bold">{{ detail.party_name }}</div>
            </div>
            <div class="col-md-4 mt-1">
              <label class="text-muted">Voucher Type</label>
              <div class="fw-bold">{{ detail.voucher_type }}</div>
            </div>
            <div class="col-md-4 mt-1">
              <label class="text-muted">Status</label>
              <div><span class="fw-bold">{{ detail.status }}</span></div>
            </div>
            <div class="col-md-4 mt-1">
              <label class="text-muted">Amount</label>
              <div class="fw-bold">{{ commaFormat(detail.total_amount) }}</div>
            </div>
            <div v-if="detail.cheque_number" class="col-md-4 mt-1">
              <label class="text-muted">Cheque No</label>
              <div class="fw-bold">{{ detail.cheque_number }}</div>
            </div>
            <div v-if="detail.note" class="col-12 mt-1">
              <label class="text-muted">Note</label>
              <div>{{ detail.note }}</div>
            </div>
          </div>

          <h5 class="mt-2">Line Items</h5>
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Account Head</th>
                <th>Note</th>
                <th class="text-end">Debit</th>
                <th class="text-end">Credit</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="! detail.ledgers || ! detail.ledgers.length">
                <td colspan="4" class="text-center">No line items.</td>
              </tr>
              <tr v-for="line in detail.ledgers" :key="line.id">
                <td>{{ line.account_head }}</td>
                <td>{{ line.note }}</td>
                <td class="text-end">{{ commaFormat(line.debit) }}</td>
                <td class="text-end">{{ commaFormat(line.credit) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="detail.attachments && detail.attachments.length" class="mt-2">
            <h5>Attachments</h5>
            <ul class="list-unstyled mb-0">
              <li v-for="att in detail.attachments" :key="att.id">
                <i class="fas fa-paperclip"></i>
                <a :href="att.file_path" target="_blank">{{ att.name }}</a>
              </li>
            </ul>
          </div>

          <h5 class="mt-2">Approval Log</h5>
          <div v-if="! detail.workflow_approval_logs || ! detail.workflow_approval_logs.length" class="text-muted">
            No approval activity yet.
          </div>
          <div v-for="log in (detail.workflow_approval_logs || [])" :key="log.id" class="mb-1">
            <LogList :approval-log="formatLog(log)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, inject } from 'vue'
import { useRoute } from 'vue-router'
import LogList from '@/components/molecule/approval-matrix/approval-log/LogList.vue'
import handlePaymentRequisition from '@/services/modules/paymentRequisition'
import figureFormatter from '@/services/utils/figureFormatter'

const props = defineProps({
  ledgerMasterId: { type: Number, required: true }
})
const emit = defineEmits(['close'])

const showError = inject('showError')
const $route = useRoute()

const { fetchPaymentRequisitionReviewDetails } = handlePaymentRequisition()
const { commaFormat } = figureFormatter()

const loadingDetail = ref(false)
const detail = ref(null)

const companyId = computed(() => $route.params.companyId)

const loadDetail = async () => {
  loadingDetail.value = true
  try {
    const res = await fetchPaymentRequisitionReviewDetails(props.ledgerMasterId, `?company_id=${companyId.value}`)
    if (! res.status) {
      showError(res.message)
      return
    }
    detail.value = res.data
  } catch (err) {
    showError('Failed to load voucher detail.')
  } finally {
    loadingDetail.value = false
  }
}

const formatLog = (log) => ({
  user: {
    name: log.user_name || '',
    email: log.user_email || ''
  },
  logged_at: log.created_at,
  status: log.status,
  comment: log.comment || ''
})

const onClickClose = () => emit('close')

const dialogRef = ref(null)
const dragOffset = ref({ x: 0, y: 0 })
const dragState = ref({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 })

const dialogStyle = computed(() => {
  if (dragOffset.value.x === 0 && dragOffset.value.y === 0) return {}
  return {
    transform: `translate(${dragOffset.value.x}px, ${dragOffset.value.y}px)`,
    margin: '1.75rem auto',
  }
})

const onMouseMove = (e) => {
  if (! dragState.value.active) return
  dragOffset.value = {
    x: dragState.value.originX + (e.clientX - dragState.value.startX),
    y: dragState.value.originY + (e.clientY - dragState.value.startY),
  }
}

const onMouseUp = () => {
  dragState.value.active = false
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
}

const onMouseDown = (e) => {
  if (e.target && e.target.closest('.btn-close')) return
  dragState.value = {
    active: true,
    startX: e.clientX,
    startY: e.clientY,
    originX: dragOffset.value.x,
    originY: dragOffset.value.y,
  }
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

onMounted(loadDetail)

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})
</script>

<style scoped>
.modal-xl {
  max-width: 1100px;
}
.draggable-dialog {
  user-select: none;
}
.draggable-handle {
  cursor: move;
}
.modal-scroll-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>
